import { StepHandle } from "..";
import { findElement } from "../../LayoutsState";
import { illustrationStepService } from "../../services/steps/illustration";
import { ColorDefinition, IllustrationElement, IllustrationStepData, Step } from "../../types";
import { WorkflowManager } from "../../WorkflowManager";
import { Variant } from "../resource/variant";

export class IllustrationStepHandle extends StepHandle<IllustrationStepData> {
    constructor(manager: WorkflowManager, step: Step<IllustrationStepData>, tags: string[]) {
        super(manager, step, tags);
    }

    /**
     * Allows for select a vector from the available vectors configured on this steps base option.
     * @param variant The new vector variant to use for this step.
     * @returns A promise that can be awaited to know when the new vector asset has been updated on the design.
     */
    async selectVariant(variant: Variant) {
        if (this.getUpdateState()) return; // Don't update this step if in the middle of updating it.
        if (this.getCurrentVariant() === variant) return;
        return illustrationStepService.selectVariant(
            this.step,
            variant.getResource(),
            this.manager.getRegionElements(this.step.stepName) || [],
            (v) => this.setUpdateState(v),
            this.manager,
        );
    }

    /**
     * Get the colors that are in the illustration.
     * @returns A list of color definitions that are currently applied to the illustration.
     */
    getColors() {
        const regionEls = this.manager.getRegionElements(this.step.stepName) || [];
        if (regionEls.length === 0) return;
        const layoutElement = findElement(regionEls[0].id, this.manager.getAllLayoutData()) as IllustrationElement;
        const elementColors = layoutElement.colors;

        // For illustrations with asset configs, return one color per channel.
        const asset = this.getCurrentVariant()?.getAssetResource();
        const assetConfiguration = asset?.assetConfiguration;
        const defaultColorVariants = assetConfiguration?.defaultColorVariants || [];
        if (defaultColorVariants.length !== 0) {
            const colors: {[key: string]: ColorDefinition} = {};
            defaultColorVariants.forEach(item => {
                // Only include channel numbers that are in use.
                const cn = assetConfiguration!.channelNumbers.find(cn => cn.number === item.channelNumber);
                if (cn) {
                    const hex = cn.id.replace(/\W/g, "");
                    colors[item.channelNumber] = { browserValue: elementColors[hex]?.browserValue || "" }
                }
            });
            return colors;
        }

        // For the standard case, return one color per color in the illustration.
        try {
            return elementColors;
        } catch (e) {
            console.error(e);
        }
    }

    /**
     * Set color for the illustration.
     */
    setColor(key: string, value: string, pmsValue?: string) {
        const newFillMap = new Map<string, string | ColorDefinition>();

        // For illustrations with asset configs, we have to map the channel number to colors in the illustration.
        const asset = this.getCurrentVariant()?.getAssetResource();
        const assetConfiguration = asset?.assetConfiguration;
        const defaultColorVariants = assetConfiguration?.defaultColorVariants || [];
        if (defaultColorVariants.length !== 0) {
            assetConfiguration!.channelNumbers.forEach(item => {
                if (item.number.toString() === key) {
                    const hex = item.id.replace(/\W/g, "");
                    if (pmsValue) {
                        newFillMap.set(hex, { browserValue: value, pmsValue });
                    } else {
                        newFillMap.set(hex, value);
                    }
                }
            });
        } else {
            // The standard case.
            if (pmsValue) {
                newFillMap.set(key, { browserValue: value, pmsValue });
            } else {
                newFillMap.set(key, value);
            }
        }

        return illustrationStepService.changeColors(
            this.step,
            this.manager.getRegionElements(this.step.stepName),
            this.manager,
            () => this.manager.getCommandContext().getAllLayouts(),
            newFillMap,
        );
    }

    async getColorOption() {
        return this.step.data.colorOption;
    }

    getAvailableColors() {
        if (!this.step.data.colorPickerEnabled) {
            return Promise.resolve([]);
        }
        return illustrationStepService.availableColors(this.step, this.manager);
    }

    isColorPickerEnabled() {
        return this.step.data.colorPickerEnabled ?? false;
    }

    isPMSPickerEnabled() {
        return this.step.data.pmsPickerEnabled ?? false;
    }
}
